import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../services/api';
import { ADMIN_OPS_REFRESH } from '../constants/adminEvents';
import { PACKAGE_TYPE_DIMENSIONS_CM } from '../constants/storeCategories';
import { fulfillmentLabel } from '../utils/fulfillment';
import './PackagingManagement.css';

const packageLabel = (code) => {
  const k = typeof code === 'string' ? code.trim() : '';
  return PACKAGE_TYPE_DIMENSIONS_CM[k]?.label || (k === '' ? '— (revisar en productos)' : k);
};

const statusBadgeClass = (status) =>
  status === 'ready_to_ship' ? 'badge-ready_to_ship' : 'badge-awaiting_packaging';

const statusBadgeText = (status) =>
  status === 'ready_to_ship' ? 'Listo legacy' : 'En empaque';

const PackagingManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [busyId, setBusyId] = useState(null);
  const [pilotMode, setPilotMode] = useState({});
  const [pilotNotes, setPilotNotes] = useState({});

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      const res = await adminService.getPackagingQueue({ limit: 80 });
      const list = res?.orders ?? [];
      setOrders(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'No se pudo cargar la cola de empaque.' });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(value || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const saveDraft = async (orderId) => {
    const isPilot = !!pilotMode[orderId];
    const note = (pilotNotes[orderId] || '').trim();
    if (!note) {
      setMessage({ type: 'error', text: 'Escribe una nota antes de guardar el borrador.' });
      return;
    }
    try {
      setBusyId(orderId);
      setMessage({ type: '', text: '' });
      await adminService.updateOrderStatus(orderId, {
        save_packaging_draft: true,
        packaging_pilot: isPilot,
        packaging_note: note
      });
      setMessage({ type: 'success', text: 'Borrador guardado en la orden (campo notas).' });
    } catch (e) {
      setMessage({
        type: 'error',
        text: e.response?.data?.message || e.message || 'Error al guardar borrador'
      });
    } finally {
      setBusyId(null);
    }
  };

  const promoteToProcessing = async (orderId) => {
    const isPilot = !!pilotMode[orderId];
    const note = (pilotNotes[orderId] || '').trim();
    if (isPilot && !note) {
      setMessage({ type: 'error', text: 'Indica la nota de excepción piloto (ej. bolsa neutra + nota).' });
      return;
    }
    const confirmMsg = isPilot
      ? '¿Marcar empaquetado en modo PILOTO (sin empaque marca) y pasar a guías y envío?'
      : '¿Marcar como empaquetado y pasar la orden a guías y envío?';
    if (!window.confirm(confirmMsg)) {
      return;
    }
    try {
      setBusyId(orderId);
      setMessage({ type: '', text: '' });
      const body = { status: 'processing' };
      if (isPilot) {
        body.packaging_pilot = true;
        body.packaging_note = note;
      }
      await adminService.updateOrderStatus(orderId, body);
      setMessage({ type: 'success', text: 'Pedido pasado a guías y envío.' });
      await fetchQueue();
      window.dispatchEvent(new Event(ADMIN_OPS_REFRESH));
    } catch (e) {
      setMessage({
        type: 'error',
        text: e.response?.data?.message || e.message || 'Error al actualizar el pedido'
      });
    } finally {
      setBusyId(null);
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm('¿Cancelar este pedido? Se restaurará el stock si aplica.')) {
      return;
    }
    try {
      setBusyId(orderId);
      setMessage({ type: '', text: '' });
      await adminService.updateOrderStatus(orderId, { status: 'cancelled' });
      setMessage({ type: 'success', text: 'Pedido cancelado.' });
      await fetchQueue();
      window.dispatchEvent(new Event(ADMIN_OPS_REFRESH));
    } catch (e) {
      setMessage({
        type: 'error',
        text: e.response?.data?.message || e.message || 'Error al cancelar'
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="packaging-management">
      <div className="container">
        <div className="section-header">
          <h2>Empaque Chía (CHK-001)</h2>
          <p>
            Solo pedidos con stock propio en bodega Chía. Los dropshipping no aparecen aquí — gestiónalos en
            Cola dropshipping. Al terminar el empaque, pasa la orden a <strong>Guías y envío</strong> (Mis Envíos
            Express o entrega Javiland local).
          </p>
        </div>

        {message.text ? (
          <div className={`message-banner ${message.type}`} role="status">
            {message.text}
          </div>
        ) : null}

        <button type="button" className="btn-refresh-pkg" onClick={() => fetchQueue()} disabled={loading}>
          Actualizar lista
        </button>

        {loading ? (
          <div className="loading-container">
            <p>Cargando cola…</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="pkg-empty">
            No hay pedidos de bodega Chía en cola de empaque en este momento.
          </div>
        ) : (
          orders.map((order) => {
            const lines = Array.isArray(order.packaging_lines) ? order.packaging_lines : [];
            const busy = busyId === order.id;
            return (
              <div key={order.id} className="order-pack-card">
                <div className="order-pack-head">
                  <div className="order-pack-meta">
                    <strong>
                      {(order.order_number || `#${order.id}`)} · {formatCurrency(order.total)}
                    </strong>
                    <span>
                      {order.customer_name || '—'} · {order.customer_email || '—'} ·{' '}
                      {formatDate(order.created_at)}
                    </span>
                  </div>
                  <span className={`badge-status ${statusBadgeClass(order.status)}`}>
                    {statusBadgeText(order.status)}
                  </span>
                </div>
                <table className="pkg-lines-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Cant.</th>
                      <th>Tipo de empaque</th>
                      <th>Despacho</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((row) => (
                      <tr key={row.id ?? `${row.product_id}-${row.product_name}`}>
                        <td>{row.product_name}</td>
                        <td>{row.quantity}</td>
                        <td className="pkg-type-cell">
                          <small>{packageLabel(row.package_type)}</small>
                        </td>
                        <td>{fulfillmentLabel(row.fulfillment)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="pkg-pilot-block">
                  <label className="pkg-pilot-check">
                    <input
                      type="checkbox"
                      checked={!!pilotMode[order.id]}
                      onChange={(e) =>
                        setPilotMode((prev) => ({ ...prev, [order.id]: e.target.checked }))
                      }
                    />
                    Excepción piloto CHK-001 (sin empaque marca físico)
                  </label>
                  <input
                    type="text"
                    className="pkg-pilot-note"
                    placeholder={
                      pilotMode[order.id]
                        ? 'Nota obligatoria en piloto: ej. bolsa neutra + nota manuscrita'
                        : 'Nota opcional de empaque (ej. instrucciones especiales)'
                    }
                    value={pilotNotes[order.id] || ''}
                    onChange={(e) =>
                      setPilotNotes((prev) => ({ ...prev, [order.id]: e.target.value }))
                    }
                  />
                </div>
                <div className="pkg-actions-bar" role="group" aria-label="Acciones de empaque">
                  <button
                    type="button"
                    className="btn-pkg-secondary"
                    disabled={busy}
                    onClick={() => saveDraft(order.id)}
                  >
                    {busy ? '…' : 'Guardar borrador'}
                  </button>
                  <button
                    type="button"
                    className="btn-pkg-done"
                    disabled={busy}
                    onClick={() => promoteToProcessing(order.id)}
                  >
                    {busy ? 'Guardando…' : 'Empaque listo → Guías y envío'}
                  </button>
                  <button
                    type="button"
                    className="btn-pkg-cancel"
                    disabled={busy}
                    onClick={() => cancelOrder(order.id)}
                  >
                    Cancelar pedido
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PackagingManagement;
